import AutocompleteFormField from '@components/Common/AutocompleteFormField';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
  WorkspaceField,
} from '@components/Common/EntityWorkspace';
import type { GlanceRow, WorkspaceTab } from '@components/Common/EntityWorkspace';
import RecordActivityCard from '@components/Common/EntityWorkspace/RecordActivityCard';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import { useWorkspaceTab } from '@components/Common/EntityWorkspace/useWorkspaceTab';
import EmptyState from '@components/Common/EmptyState';
import ErrorMessage from '@components/Common/ErrorMessage';
import RecordLink from '@components/Common/RecordLink';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import type { ColumnsConfig } from '@components/Common/Table/cardFields';
import { useToast } from '@components/Common/Toast/ToastProvider';
import ToggleField from '@components/Common/ToggleField';
import WorkspaceInput from '@components/Common/WorkspaceInput';
import { getPromoCodesQuery, getPromoRedemptionsQuery, updatePromoCodeMutation } from '@constants/graphQLQueries';
import { formatDate } from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { siteOrigin } from '@helpers/siteOrigin';
import DiscountOutlinedIcon from '@mui/icons-material/DiscountOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import HistoryIcon from '@mui/icons-material/History';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Chip } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  type PromoCode,
  type PromoForm,
  type PromoRedemptionRow,
  expiryLabel,
  promoFormErrors,
  promoFormFrom,
  redemptionsLabel,
} from './promoCodes';
import { fewestOpenJobsHint, useServedCities } from './useServedCities';
import './index.scss';

/**
 * One promo code, as an entity workspace: its settings edited in place, who redeemed it, and its
 * audit trail. Super Admin only. The code itself never changes once created: it is printed on the
 * offer page and stored on every redemption.
 */

const TABS: WorkspaceTab[] = [
  { id: 'settings', label: 'Settings', icon: <TuneOutlinedIcon /> },
  { id: 'redemptions', label: 'Redemptions', icon: <PeopleOutlinedIcon /> },
  { id: 'history', label: 'History', icon: <HistoryIcon /> },
];

const SECTION_LABELS: Record<string, string> = { offer: 'Offer', limits: 'Limits' };

const REDEMPTION_COLUMNS = ['employerName', 'companyName', 'postedCity', 'redeemedAt', 'status'];
const REDEMPTION_CARD: ColumnsConfig = {
  employerName: { card: 'title', tablet: true },
  status: { card: 'status', tablet: true },
  postedCity: { tablet: true },
};
const REDEMPTIONS_PAGE_SIZE = 20;

type FormErrors = Partial<Record<keyof PromoForm, string>>;

const PromoCodeWorkspace: React.FC = () => {
  const { promoId } = useParams<{ promoId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { cities, openJobs, loading: citiesLoading } = useServedCities();

  const [promo, setPromo] = useState<PromoCode | null>(null);
  const [form, setForm] = useState<PromoForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useWorkspaceTab(TABS, 'settings');
  const [activityOpen, toggleActivity] = useActivityRail();
  const [editing, setEditing] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const snapshot = useRef<PromoForm | null>(null);

  const [redemptions, setRedemptions] = useState<PromoRedemptionRow[]>([]);
  const [redemptionTotal, setRedemptionTotal] = useState(0);
  const [redemptionPage, setRedemptionPage] = useState(0);
  /* Nothing has come back yet: the tab shows a skeleton, never "nobody has redeemed" before it knows. */
  const [redemptionsLoaded, setRedemptionsLoaded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchGraphQLApiData(getPromoCodesQuery());
      const found = (res?.data?.promoCodes as PromoCode[] | undefined)?.find((p) => p._id === promoId) ?? null;
      if (!found) setError('This promo code no longer exists.');
      setPromo(found);
      setForm(found ? promoFormFrom(found) : null);
    } catch (e: any) {
      console.error('[PromoCodeWorkspace] load failed', e?.message);
      setError('Could not load the promo code. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [promoId]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadRedemptions = useCallback(async (code: string, page: number) => {
    try {
      const res = await fetchGraphQLApiData(getPromoRedemptionsQuery(code, page, REDEMPTIONS_PAGE_SIZE));
      setRedemptions(res?.data?.promoRedemptions?.content ?? []);
      setRedemptionTotal(Number(res?.data?.promoRedemptions?.totalElements ?? 0));
    } catch (e: any) {
      console.error('[PromoCodeWorkspace] redemptions failed', e?.message);
      showToast('Could not load redemptions', 'error');
    } finally {
      setRedemptionsLoaded(true);
    }
  }, [showToast]);

  useEffect(() => {
    if (promo?.code && activeTab === 'redemptions') void loadRedemptions(promo.code, redemptionPage);
  }, [promo?.code, activeTab, redemptionPage, loadRedemptions]);

  if (loading && !promo) return <SkeletonLoader variant="workspace" />;
  if (error && !promo) return <ErrorMessage page message={error} onRetry={load} />;
  if (!promo || !form) return null;

  const setField = <K extends keyof PromoForm>(key: K, value: PromoForm[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setDirty(true);
  };

  const startEdit = (id: string) => {
    if (editing && editing !== id && dirty) {
      showToast(`Save or discard ${SECTION_LABELS[editing] ?? 'the open section'} first`, 'info');
      return;
    }
    snapshot.current = { ...form };
    setEditing(id);
    setDirty(false);
    setErrors({});
  };

  const cancelEdit = () => {
    if (snapshot.current) setForm(snapshot.current);
    setEditing(null);
    setDirty(false);
    setErrors({});
  };

  /** One save path; updatePromoCode replaces every setting, so the whole form goes. */
  const save = async () => {
    const found = promoFormErrors(form, { requireCode: false, savedExpiresAt: promoFormFrom(promo).expiresAt });
    if (Object.values(found).some(Boolean)) {
      setErrors(found);
      return;
    }
    setSaving(true);
    try {
      const res = await fetchGraphQLApiData(updatePromoCodeMutation(promo._id, form));
      const updated: PromoCode | undefined = res?.data?.updatePromoCode;
      if (updated) {
        setPromo(updated);
        setForm(promoFormFrom(updated));
      }
      setEditing(null);
      setDirty(false);
      showToast('Promo code saved', 'success');
    } catch (e: any) {
      // The backend's refusals are written for this screen ("These aren't served cities: ...").
      showToast(String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '') || 'Could not save. Nothing was changed.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const isEditing = (id: string) => editing === id;
  // The site's offer page advertises MUMBAI1 by default; any other code is named in the URL.
  const offerUrl = `${siteOrigin()}/post-a-job/mumbai${promo.code === 'MUMBAI1' ? '' : `?code=${encodeURIComponent(promo.code)}`}`;

  const glance: GlanceRow[] = [
    { key: 'Redemptions', value: redemptionsLabel(promo), mono: true },
    { key: 'Expires', value: expiryLabel(promo.expiresAt), mono: !!promo.expiresAt },
    { key: 'Free post open for', value: `${promo.jobValidityDays ?? 30} days`, mono: true },
    { key: 'Cities', value: String(promo.targetCities.length), mono: true },
  ];

  const identityRail = (
    <IdentityRail
      initials={promo.code.slice(0, 2)}
      name={promo.code}
      subId={promo.description || undefined}
      statusChip={<CRMStatusChip status={promo.status} />}
      glance={glance}
      audit={{
        createdAt: promo.createdAt ?? undefined,
        updatedAt: promo.updatedAt ?? undefined,
      }}
    />
  );

  const settings = (
    <>
      <SectionCard
        title="Offer"
        icon={<DiscountOutlinedIcon />}
        editing={isEditing('offer')}
        onEdit={() => startEdit('offer')}
        onCancel={cancelEdit}
        onSave={save}
      >
        <WorkspaceField label="Code" mono value={promo.code} />
        <WorkspaceField
          label="Active"
          value={<CRMStatusChip status={form.active ? 'active' : 'inactive'} />}
          editing={isEditing('offer')}
          edit={(
            <ToggleField
              name="active"
              label={form.active ? 'Employers can redeem it' : 'Switched off'}
              checked={form.active}
              onChange={(checked) => setField('active', checked)}
            />
          )}
        />
        <WorkspaceField
          label="Target cities"
          full
          value={(
            <span className="promo-cities">
              {promo.targetCities.map((city) => <Chip key={city} size="small" label={city} />)}
            </span>
          )}
          editing={isEditing('offer')}
          edit={(
            <AutocompleteFormField
              required
              multiple
              name="targetCities"
              label=""
              loading={citiesLoading}
              options={cities}
              value={form.targetCities}
              onChange={(_e, value) => setField('targetCities', Array.isArray(value) ? value : [])}
              error={!!errors.targetCities}
              helperText={errors.targetCities
                ?? fewestOpenJobsHint(openJobs)
                ?? 'Employers who already redeemed keep the cities they were given.'}
            />
          )}
        />
        <WorkspaceField
          label="Description"
          full
          value={promo.description || '-'}
          editing={isEditing('offer')}
          edit={<WorkspaceInput value={form.description} onChange={(v) => setField('description', v)} />}
        />
      </SectionCard>

      <SectionCard
        title="Limits"
        icon={<EventOutlinedIcon />}
        editing={isEditing('limits')}
        onEdit={() => startEdit('limits')}
        onCancel={cancelEdit}
        onSave={save}
      >
        <WorkspaceField
          label="Expires on"
          value={expiryLabel(promo.expiresAt)}
          mono={!!promo.expiresAt}
          editing={isEditing('limits')}
          edit={(
            <WorkspaceInput
              type="date"
              value={form.expiresAt}
              error={!!errors.expiresAt}
              helperText={errors.expiresAt ?? 'Leave empty for no expiry.'}
              onChange={(v) => setField('expiresAt', v)}
            />
          )}
        />
        <WorkspaceField
          label="Max redemptions"
          value={promo.maxRedemptions != null ? String(promo.maxRedemptions) : 'No limit'}
          mono={promo.maxRedemptions != null}
          editing={isEditing('limits')}
          edit={(
            <WorkspaceInput
              type="number"
              value={form.maxRedemptions}
              error={!!errors.maxRedemptions}
              helperText={errors.maxRedemptions ?? 'Leave empty for no limit.'}
              onChange={(v) => setField('maxRedemptions', v)}
            />
          )}
        />
        <WorkspaceField
          label="Free post open for"
          value={`${promo.jobValidityDays ?? 30} days`}
          mono
          editing={isEditing('limits')}
          edit={(
            <WorkspaceInput
              type="number"
              value={form.jobValidityDays}
              error={!!errors.jobValidityDays}
              helperText={errors.jobValidityDays ?? 'Days. A paid single post stays open for 2.'}
              onChange={(v) => setField('jobValidityDays', v)}
            />
          )}
        />
      </SectionCard>

      <SectionCard title="Where employers see it" icon={<LinkOutlinedIcon />}>
        <WorkspaceField
          label="Offer page"
          full
          mono
          value={<a href={offerUrl} target="_blank" rel="noopener noreferrer">{offerUrl}</a>}
        />
        <WorkspaceField
          label="Applies to"
          full
          value="One free post per employer and per company, for a job located in a target city or one of its areas. Anywhere else they pay as usual."
        />
      </SectionCard>
    </>
  );

  const redemptionsTab = (
    <SectionCard
      title="Redemptions"
      icon={<PeopleOutlinedIcon />}
      meta={`${redemptionsLoaded ? redemptionTotal : promo.redemptionCount} total`}
    >
      {!redemptionsLoaded ? (
        <SkeletonLoader rows={5} columns={REDEMPTION_COLUMNS.length} />
      ) : redemptionTotal === 0 ? (
        <EmptyState compact title="Nobody has redeemed this code yet" />
      ) : (
        <MintlyTable
          pagination={{
            enabled: true,
            page: redemptionPage,
            rowsPerPage: REDEMPTIONS_PAGE_SIZE,
            totalNumberOfRows: redemptionTotal,
            handleChangePage: (_e: unknown, next: number) => setRedemptionPage(next),
          }}
          rows={redemptions.map((r) => ({ ...r, id: r.subscriptionId }))}
          selectors={REDEMPTION_COLUMNS}
          columns={REDEMPTION_CARD}
          tableHeadRow={{
            employerName: <>Employer</>,
            companyName: <>Company</>,
            postedCity: <>Posted in</>,
            redeemedAt: <>Redeemed</>,
            status: <>Status</>,
          }}
          tableBodyRow={{
            employerName: (row: PromoRedemptionRow) => (row.employerId
              ? <RecordLink to={`/employers/${row.employerId}`}>{row.employerName || row.employerId}</RecordLink>
              : <>-</>),
            companyName: (row: PromoRedemptionRow) => (row.companyId
              ? <RecordLink to={`/companies/${row.companyId}`}>{row.companyName || row.companyId}</RecordLink>
              : <>-</>),
            postedCity: (row: PromoRedemptionRow) => (row.postedCity
              ? <>{row.postedCity}</>
              : <CRMStatusChip status="pending" label="Not posted yet" />),
            redeemedAt: (row: PromoRedemptionRow) => <>{formatDate(row.redeemedAt)}</>,
            status: (row: PromoRedemptionRow) => <CRMStatusChip status={row.status || 'ACTIVE'} />,
          }}
        />
      )}
    </SectionCard>
  );

  // The backend audits promo changes and redemptions against the code, not the id.
  const history = <RecordActivityCard recordId={promo.code} />;

  const tabContent: Record<string, React.ReactNode> = { settings, redemptions: redemptionsTab, history };

  return (
    <EntityWorkspace
      backLabel="Promo codes"
      onBack={() => navigate('/promo-codes')}
      identityRail={identityRail}
      tabs={TABS}
      activeTabId={activeTab}
      onTabChange={setActiveTab}
      /* No invented timeline: the record's real history is the History tab's audit trail. */
      activityRail={<ActivityRail timeline={[]} />}
      activityOpen={activityOpen}
      onToggleActivity={toggleActivity}
      isDirty={dirty && !!editing && !saving}
      dirtySectionLabel={editing ? SECTION_LABELS[editing] ?? 'Section' : undefined}
      onDiscard={cancelEdit}
      onSave={save}
    >
      {tabContent[activeTab]}
    </EntityWorkspace>
  );
};

export default PromoCodeWorkspace;
